import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    MenuItem,
    Select,
    FormControl,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import apiClient from "../../services/apiClient";
import moment from "moment";

interface AppointmentDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (formData: any) => void;
    saving?: boolean;
}

// Validation schema - only require vet, parent, and pet
const AppointmentSchema = Yup.object().shape({
    vetId: Yup.string().required("Vet is required"),
    parentId: Yup.string().required("Parent is required"),
    petId: Yup.string().required("Pet is required"),
});

const defaultInitialValues = {
    vetId: "",
    parentId: "",
    petId: "",
};

const EmergencyDialog: React.FC<AppointmentDialogProps> = ({
    open,
    onClose,
    onSave,
    saving = false,
}) => {
    const [vets, setVets] = useState<any[]>([]);
    const [loadingVets, setLoadingVets] = useState(false);
    const [parents, setParents] = useState<any[]>([]);
    const [loadingParents, setLoadingParents] = useState(false);
    const [pets, setPets] = useState<any[]>([]);
    const [loadingPets, setLoadingPets] = useState(false);
    const [selectedParentId, setSelectedParentId] = useState("");

    // Fetch initial data
    useEffect(() => {
        if (open) {
            fetchVets();
            fetchParents();
        }
    }, [open]);

    // Fetch pets when parentId changes
    useEffect(() => {
        if (selectedParentId) fetchPets(selectedParentId);
        else setPets([]);
    }, [selectedParentId]);

    // Fetch vets
    const fetchVets = async () => {
        setLoadingVets(true);
        try {
            const params = {
                limit: "100",
                page: "1",
                searchkey: "",
                order_by: "id",
                dir: "2",
                isActiveUsers: "1"
            };
            const queryString = new URLSearchParams(params).toString();
            const { data } = await apiClient(`v1/resource/providers/all?${queryString}`, {
                method: "GET"
            }) as any;
            if (Array.isArray(data.providers)) {
                setVets(
                    data.providers.map((item: any) => ({
                        label: `${item.user.first_name} ${item.user.last_name}`,
                        value: item.user_id.toString(),
                        user_id: item.user_id,
                        id: item.id,
                    }))
                );
            }
        } catch (err) {
            console.error("Error fetching vets:", err);
        } finally {
            setLoadingVets(false);
        }
    };

    // Fetch parents
    const fetchParents = async () => {
        setLoadingParents(true);
        try {
            const params = {
                limit: "100",
                page: "1",
                searchkey: "",
                order_by: "id",
                dir: "2",
                isActiveUsers: "1"
            };
            const queryString = new URLSearchParams(params).toString();
            const { data } = await apiClient(`v1/resource/patients/all?${queryString}`, {
                method: "GET"
            }) as any;
            if (Array.isArray(data.patients)) {
                setParents(
                    data.patients.map((item: any) => ({
                        label: `${item.user.first_name} ${item.user.last_name}`,
                        value: item.user_id.toString(),
                        id: item.id,
                    }))
                );
            }
        } catch (err) {
            console.error("Error fetching parents:", err);
        } finally {
            setLoadingParents(false);
        }
    };

    // Fetch pets
    const fetchPets = async (parentId: string) => {
        setLoadingPets(true);
        try {
            const params = {
                primary_id: parentId,
                limit: "100",
                page: "1",
                status: "active"
            };
            const queryString = new URLSearchParams(params).toString();
            const { data } = await apiClient(`v1/resource/pets/all?${queryString}`, {
                method: "GET"
            }) as any;
            if (Array.isArray(data.pets)) {
                setPets(
                    data.pets.map((item: any) => ({
                        label: item?.additional_info?.name,
                        value: item.id.toString(),
                    }))
                );
            }
        } catch (err) {
            console.error("Error fetching pets:", err);
        } finally {
            setLoadingPets(false);
        }
    };

    const transformPayload = (values: any) => {
        const selectedVet = vets.find((v) => v.value === values.vetId);
        const selectedParent = parents.find((p) => p.value === values.parentId);
        const selectedPet = pets.find((p) => p.value === values.petId);

        // Get current date and time
        const currentDateTime = moment().add(5, 'minutes').format("YYYY-MM-DD HH:mm:ss");

        return {
            provider_id: Number(selectedVet?.value) || 0,
            patient_id: Number(selectedPet?.value) || 0,
            patient_name: selectedPet?.label || "",
            consult_doctor: selectedVet?.label || "Unknown",
            consult_date_time: currentDateTime,
            consult_type: "Emergency",
            charge_code_id: 0,
            reason_for_consult: "Emergency Consult",
            payment: {
                name: "",
                code: "",
                amount: 0,
                total_amount: 600,
                taxes: [],
                tax_total: 0,
                charge_code_id: 0,
            },
            consult_data: {
                type: "virtual",
            },
            additional_info: {
                owner_name: selectedParent?.label || "",
                is_emergency: true,
            },
        };
    };

    // Generic select field
    const renderSelectField = (
        label: string,
        name: string,
        options: any[],
        loading: boolean,
        onChangeCallback?: (value: string) => void
    ) => (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
                {label}
            </Typography>
            <Field name={name}>
                {({ field, form, meta }: any) => (
                    <>
                        <FormControl
                            fullWidth
                            error={meta.touched && Boolean(meta.error)}
                            sx={{
                                flex: 1,
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "12px",
                                    background: "#fafafa",
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                                    transition: "0.3s",
                                    "&:hover": {
                                        boxShadow: "0 4px 10px rgba(0,0,0,0.15)"
                                    },
                                    "&.Mui-focused": {
                                        boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.3)"
                                    },
                                },
                            }}
                        >
                            {loading ? (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : (
                                <Select
                                    {...field}
                                    value={field.value || ""}
                                    onChange={(e) => {
                                        form.setFieldValue(name, e.target.value as string);
                                        if (onChangeCallback) onChangeCallback(e.target.value as string);
                                    }}
                                >
                                    {options.map((item) => (
                                        <MenuItem key={item.value} value={item.value}>
                                            {item.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        </FormControl>
                        {meta.touched && meta.error && (
                            <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1.5 }}>
                                {meta.error}
                            </Typography>
                        )}
                    </>
                )}
            </Field>
        </Box>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ textAlign: "center", fontWeight: "bold", fontSize: "1.5rem" }}>
                Emergency Consultation
            </DialogTitle>

            <Formik
                initialValues={defaultInitialValues}
                validationSchema={AppointmentSchema}
                onSubmit={(values) => {
                    const payload = transformPayload(values);
                    console.log("Submitted payload:", JSON.stringify(payload, null, 2));
                    onSave(payload);
                }}
            >
                {({ handleSubmit, values, setFieldValue }) => (
                    <Form onSubmit={handleSubmit}>
                        <DialogContent dividers>
                            <Box sx={{ mb: 3, p: 2, backgroundColor: "#e3f2fd", borderRadius: 2 }}>
                                <Typography variant="body2" color="text.secondary" textAlign="center">
                                    <strong>Consult Type:</strong> Virtual | <strong>Reason:</strong> Emergency Consult | <strong>Amount:</strong> ₹600
                                </Typography>
                            </Box>

                            {/* Veterinarian Selection */}
                            <Box sx={{ mb: 3 }}>
                                {renderSelectField(
                                    "Select Veterinarian *",
                                    "vetId",
                                    vets,
                                    loadingVets
                                )}
                            </Box>

                            {/* Parent Selection */}
                            <Box sx={{ mb: 3 }}>
                                {renderSelectField(
                                    "Select Parent *",
                                    "parentId",
                                    parents,
                                    loadingParents,
                                    (value) => {
                                        const parentObj = parents.find((p) => p.value === value);
                                        const actualParentId = parentObj?.id ?? "";
                                        setSelectedParentId(actualParentId);
                                        // Reset pet selection when parent changes
                                        setFieldValue("petId", "");
                                    }
                                )}
                            </Box>

                            {/* Pet Selection */}
                            <Box sx={{ mb: 3 }}>
                                {renderSelectField(
                                    "Select Pet *",
                                    "petId",
                                    pets,
                                    loadingPets
                                )}
                            </Box>
                        </DialogContent>

                        <DialogActions sx={{ justifyContent: "center", p: 2 }}>
                            <Button
                                onClick={onClose}
                                color="secondary"
                                variant="outlined"
                                sx={{ minWidth: 100 }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={saving}
                                sx={{ minWidth: 100 }}
                            >
                                {saving ? "Creating..." : "Create Emergency Consult"}
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export default EmergencyDialog;